import { useState, useRef, useEffect } from "react";
import { PlayIcon, PauseIcon, DownloadIcon } from "assets/svgs";
import { NotificationManager } from "react-notifications";
import styles from "./styles.module.css";

export function AudioPlayer({ audioSrc, chatId, activeId, setActiveId }) {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [fileSizeKB, setFileSizeKB] = useState<null | number>(null);
  const audioRef = useRef<any>(null);

  useEffect(() => {
    let audioVar: any;
    if (audioRef?.current) {
      audioVar = audioRef.current;
      audioVar.addEventListener("timeupdate", handleTimeUpdate);
      audioVar.addEventListener("loadedmetadata", () => {
        setDuration(Math.ceil(audioVar.duration));
      });
    }

    return () => {
      audioVar?.removeEventListener("timeupdate", handleTimeUpdate);
      audioVar.removeEventListener("loadedmetadata", () => {
        setDuration(Math.ceil(audioVar.duration));
      });
    };
  }, []);

  // It will stop the audio after completion
  useEffect(() => {
    if (currentTime === duration) {
      setIsPlaying(false);
    }
  }, [currentTime, duration]);

  //   It will pause the previous audio
  useEffect(() => {
    if (activeId !== chatId) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [activeId, chatId, audioSrc]);

  useEffect(() => {
    fetch(audioSrc, { method: "HEAD" })
      .then((response) => {
        if (!response.ok)
          throw new Error(
            `Audio File not found with this name ${
              response?.url?.split("/").slice(-1)[0] || ""
            }`
          );

        const contentLength = response.headers.get("content-length");
        if (contentLength) {
          const sizeKB = parseInt(contentLength) / 1024;

          setFileSizeKB(sizeKB);
        } else {
          setFileSizeKB(null);
        }
      })
      .catch((error) => {
        NotificationManager.error(error.message);
        setFileSizeKB(null);
      });
  }, [audioSrc]);

  function handlePlay() {
    setActiveId(chatId);
    audioRef.current.play();
    setIsPlaying(true);
  }

  function handlePause() {
    audioRef.current.pause();
    setIsPlaying(false);
  }

  function handlePlayPause() {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  }

  function handleTimeUpdate() {
    const newTime = audioRef.current.currentTime;
    const newSeconds = Math.ceil(newTime);

    if (newSeconds) {
      setCurrentTime(newSeconds);
    }
  }

  function handleSeek(e: any) {
    audioRef.current.currentTime = e.target.value;
    setCurrentTime(e.target.value);
  }

  function formatDuration(durationSeconds: number) {
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = Math.floor(durationSeconds % 60);

    const formattedSeconds = seconds.toString().padStart(2, "0");
    return `${minutes}:${formattedSeconds}`;
  }

  const containerClass = `${styles.audioPlayerContainer} ${
    fileSizeKB > 0 ? "" : styles.disabledClass
  }`;

  return (
    <div className={containerClass}>
      <button onClick={handlePlayPause}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </button>

      <input
        type="range"
        min="0"
        max={duration}
        value={currentTime}
        onChange={handleSeek}
      />

      <audio ref={audioRef} src={audioSrc} />

      <div className={styles.duration}>
        <aside className={styles.text}>{formatDuration(currentTime)}</aside> /
        <aside className={styles.text}>{formatDuration(duration)}</aside>
      </div>

      {/* <a
        href={audioSrc}
        download={`audio-${chatId}.mp3`} // Customize the downloaded file name
        className={styles.downloadButton}
        target="_blank"
        rel="noreferrer noopener"
      >
        <DownloadIcon />
      </a> */}
    </div>
  );
}
